import {
    ApiFriendStealReqV2Schema,
    ApiFriendStealResV2Schema,
    ApiFriendStealTargetsResV2Schema,
    type ApiFriendStealGrant,
    type ApiFriendStealResV2,
    type ApiFriendStealTargetV2,
} from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { authenticatedApiCall } from "../helpers/api";
import i18n from "../i18n/i18n";

export class FriendStealApiError extends Error {
    constructor(
        message: string,
        readonly response?: Extract<ApiFriendStealResV2, { status: "ERROR" }>,
    ) {
        super(message);
        this.name = "FriendStealApiError";
    }
}

function reconcilePairRetryAt(
    targets: ApiFriendStealTargetV2[],
    knownPairRetryAt: ReadonlyMap<string, string>,
    now: number,
): ApiFriendStealTargetV2[] {
    return targets.map((target) => {
        const retryAt = knownPairRetryAt.get(target.userId);
        if (!retryAt) return target;

        const retryTime = Date.parse(retryAt);
        if (!Number.isFinite(retryTime) || retryTime <= now) return target;

        const currentRetryTime = target.nextStealAt
            ? Date.parse(target.nextStealAt)
            : Number.NEGATIVE_INFINITY;
        if (currentRetryTime >= retryTime) return target;

        return { ...target, nextStealAt: retryAt };
    });
}

export function useFriendSteals(enabled: boolean) {
    const { state: slotState, setSlice: setSlotMachineSlice } =
        useSlice("slotMachineSlice");
    const [targets, setTargets] = useState<ApiFriendStealTargetV2[]>([]);
    const [grants, setGrants] = useState<ApiFriendStealGrant[]>([]);
    const [loadStatus, setLoadStatus] = useState<
        "idle" | "loading" | "ready" | "error"
    >("idle");
    const [loadError, setLoadError] = useState<string | null>(null);
    const [submittingUserId, setSubmittingUserId] = useState<string | null>(
        null,
    );
    const requestRef = useRef<AbortController | null>(null);
    const pairRetryAtRef = useRef(new Map<string, string>());

    const refresh = useCallback(async () => {
        if (!enabled) return;
        requestRef.current?.abort();
        const controller = new AbortController();
        requestRef.current = controller;
        setLoadStatus("loading");
        setLoadError(null);
        setGrants([]);
        try {
            const { body } = await authenticatedApiCall(
                "/api/v2/friend-steals/targets?limit=100",
                ApiFriendStealTargetsResV2Schema,
                { signal: controller.signal },
            );
            if (body.status === "ERROR") {
                throw new FriendStealApiError(
                    i18n.t("friendSteal.errors.targets", {
                        code: body.code,
                    }),
                );
            }
            if (requestRef.current !== controller) return;
            setTargets(
                reconcilePairRetryAt(
                    body.targets,
                    pairRetryAtRef.current,
                    Date.now(),
                ),
            );
            setGrants(body.grants);
            setSlotMachineSlice((current) =>
                current.snapshot
                    ? {
                          snapshot: {
                              ...current.snapshot,
                              friendStealInventory: body.inventory,
                          },
                      }
                    : {},
            );
            setLoadStatus("ready");
        } catch (error) {
            if (controller.signal.aborted) return;
            setLoadStatus("error");
            setLoadError(
                error instanceof FriendStealApiError
                    ? error.message
                    : i18n.t("friendSteal.errors.targets", {
                          code: "UNKNOWN",
                      }),
            );
        } finally {
            if (requestRef.current === controller) requestRef.current = null;
        }
    }, [enabled, setSlotMachineSlice]);

    useEffect(() => {
        if (enabled) void refresh();
        return () => requestRef.current?.abort();
    }, [enabled, refresh]);

    const steal = useCallback(
        async (victimUserId: string) => {
            const request = ApiFriendStealReqV2Schema.parse({
                requestId: crypto.randomUUID(),
                victimUserId,
            });
            const perform = () =>
                authenticatedApiCall(
                    "/api/v2/friend-steals",
                    ApiFriendStealResV2Schema,
                    { method: "POST", body: JSON.stringify(request) },
                );

            setSubmittingUserId(victimUserId);
            try {
                let response;
                try {
                    response = await perform();
                } catch {
                    // Transport retry must retain the same requestId.
                    try {
                        response = await perform();
                    } catch {
                        throw new FriendStealApiError(
                            i18n.t("friendSteal.errors.action", {
                                code: "UNKNOWN",
                            }),
                        );
                    }
                }
                const body = response.body;
                if (body.status === "ERROR") {
                    if (body.code === "PAIR_COOLDOWN" && body.retryAt) {
                        pairRetryAtRef.current.set(victimUserId, body.retryAt);
                        setTargets((current) =>
                            reconcilePairRetryAt(
                                current,
                                pairRetryAtRef.current,
                                Date.now(),
                            ),
                        );
                    }
                    if (body.inventory) {
                        const inventory = body.inventory;
                        setSlotMachineSlice((current) =>
                            current.snapshot
                                ? {
                                      snapshot: {
                                          ...current.snapshot,
                                          friendStealInventory: inventory,
                                      },
                                  }
                                : {},
                        );
                    }
                    throw new FriendStealApiError(
                        body.code === "PAIR_COOLDOWN"
                            ? i18n.t("friendSteal.errors.pairLimitReached")
                            : i18n.t("friendSteal.errors.action", {
                                  code: body.code,
                              }),
                        body,
                    );
                }

                setSlotMachineSlice((current) =>
                    current.snapshot
                        ? {
                              snapshot: {
                                  ...current.snapshot,
                                  balances: body.balances,
                                  friendStealInventory: body.inventory,
                                  observedAt: body.settledAt,
                              },
                          }
                        : {},
                );
                // Avoid a replica read immediately after settlement. The
                // publisher already revalidated the pair, and this prevents a
                // second same-pair attempt during the current dialog session.
                setTargets((current) =>
                    current.filter((target) => target.userId !== victimUserId),
                );
                return body;
            } finally {
                setSubmittingUserId(null);
            }
        },
        [setSlotMachineSlice],
    );

    return {
        targets,
        grants,
        inventory: slotState.snapshot?.friendStealInventory,
        loadStatus,
        loadError,
        refresh,
        steal,
        submittingUserId,
    };
}
